import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PayOnlineNotice, UpiPayQR, usePayOptions } from "./PayBlock";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  listBillingAccounts,
  listUnpaidInvoices,
  recordPayment,
  type RecordedPayment,
  type UnpaidInvoice,
} from "../../api/payments";
import {
  parseAmount,
  paymentDraftProblems,
  receivedAtFrom,
  todayIST,
  type PaymentDraft,
  type PaymentMethod,
} from "../../domain/paymentDraft";

type Mode = "invoice" | "advance";

const inr = (n: number) => n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** An IST wall-clock datetime string as dd-mm-yyyy, without re-zoning it. */
const ddmmyyyy = (s: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

function errorTitle(e: Error, what: string): string {
  if (e instanceof ApiError) {
    // 409 from radpayment: this UTR is already recorded for a different amount.
    if (e.status === 409) return "This payment reference is already recorded";
    switch (e.kind) {
      case "fail-closed":
        return `Nothing was sent for ${what}`;
      case "unauthenticated":
        return "Your sign-in has expired -- sign in again";
      case "forbidden":
        return "Refused -- your role cannot record payments here";
      case "not-found":
        return "This route is not published on the gateway yet";
      case "at-capacity":
        return "The service is busy -- try again shortly";
      case "bad-request":
        return `The request for ${what} was refused`;
      case "unavailable":
        return `Could not reach the service for ${what}`;
    }
  }
  return `Could not complete ${what}`;
}

function ErrorAlert({ error, what }: { error: Error; what: string }) {
  return (
    <Alert severity="error" sx={{ mb: 2 }}>
      <AlertTitle>{errorTitle(error, what)}</AlertTitle>
      {error.message}
    </Alert>
  );
}

/**
 * Record payment: a UPI or cash payment received in the operator's namespace,
 * against an unpaid invoice or as an advance (owner decision 2026-09-15).
 *
 * The receipt is issued by radbilling's sweeper and emailed by radcomm within
 * about five minutes; its Recorded By is the signed-in person, resolved at the
 * gateway from the token -- nothing in this form names who recorded it.
 */
export function RecordPaymentScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const payOptions = usePayOptions(ns);
  const qc = useQueryClient();

  const [mode, setMode] = useState<Mode>("invoice");
  const [draft, setDraft] = useState<PaymentDraft | null>(null);
  const [label, setLabel] = useState("");
  const [due, setDue] = useState<number | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [advanceAccount, setAdvanceAccount] = useState("");
  const [recorded, setRecorded] = useState<{ payment: RecordedPayment; label: string } | null>(null);

  const enabled = Boolean(identity.token && ns);
  const unpaid = useQuery({
    queryKey: ["unpaid-invoices", ns],
    queryFn: ({ signal }) => listUnpaidInvoices(identity, ns, signal),
    enabled,
    retry: false,
  });
  const accounts = useQuery({
    queryKey: ["billing-accounts", ns],
    queryFn: ({ signal }) => listBillingAccounts(identity, ns, signal),
    enabled: enabled && mode === "advance",
    retry: false,
  });

  const mutation = useMutation<RecordedPayment, Error, PaymentDraft>({
    mutationFn: (d) =>
      recordPayment(identity, ns, {
        accountId: d.accountId,
        invoiceId: d.invoiceId,
        method: d.method,
        reference: d.reference,
        amount: parseAmount(d.amount) ?? 0,
        receivedAt: receivedAtFrom(d.receivedDate, d.receivedTime),
      }),
    onSuccess: (payment) => {
      setRecorded({ payment, label });
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ["unpaid-invoices", ns] });
    },
  });

  if (!ns) {
    return (
      <Alert severity="error">
        No namespace could be resolved for your sign-in, so there is nowhere to record a payment.
      </Alert>
    );
  }

  const open = (d: PaymentDraft, title: string, amountDue: number | null) => {
    mutation.reset();
    setAttempted(false);
    setLabel(title);
    setDue(amountDue);
    setDraft(d);
  };

  const startForInvoice = (inv: UnpaidInvoice) =>
    open(
      {
        accountId: inv.accountid,
        invoiceId: inv.id,
        method: "upi",
        reference: "",
        amount: String(inv.amountdue),
        receivedDate: todayIST(),
        receivedTime: "",
      },
      `Invoice ${inv.invoicenumber} -- ${inv.buyername || inv.accountid}`,
      inv.amountdue,
    );

  const startAdvance = () => {
    const acct = accounts.data?.find((a) => a.accountid === advanceAccount);
    open(
      {
        accountId: advanceAccount,
        invoiceId: null,
        method: "upi",
        reference: "",
        amount: "",
        receivedDate: todayIST(),
        receivedTime: "",
      },
      `Advance -- ${acct?.name || advanceAccount}`,
      null,
    );
  };

  const set = <K extends keyof PaymentDraft>(k: K, v: PaymentDraft[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));
  const problems = draft ? paymentDraftProblems(draft) : [];

  const submit = () => {
    setAttempted(true);
    if (draft && problems.length === 0) mutation.mutate(draft);
  };

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Record payment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record a UPI or cash payment received in <strong>{ns}</strong>. The receipt is issued and emailed to the
        customer within about five minutes, and names you as the person who recorded it.
      </Typography>

      {recorded && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRecorded(null)} data-testid="payment-recorded">
          <AlertTitle>Payment recorded</AlertTitle>
          {recorded.label}: ₹{inr(recorded.payment.amount)}, status {recorded.payment.status}. Reference{" "}
          {recorded.payment.connectorreference || recorded.payment.id}.
        </Alert>
      )}

      <Tabs value={mode} onChange={(_, v: Mode) => setMode(v)} sx={{ mb: 2 }}>
        <Tab value="invoice" label="Against an invoice" />
        <Tab value="advance" label="Advance (no invoice)" />
      </Tabs>

      {mode === "invoice" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          {unpaid.isPending && <Typography>Loading unpaid invoices…</Typography>}
          {unpaid.isError && <ErrorAlert error={unpaid.error} what="the unpaid invoices" />}
          {unpaid.isSuccess && unpaid.data.length === 0 && (
            <Alert severity="info">Nothing is owed: there are no unpaid invoices in {ns}.</Alert>
          )}
          {unpaid.isSuccess && unpaid.data.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Total (₹)</TableCell>
                    <TableCell align="right">Paid (₹)</TableCell>
                    <TableCell align="right">Due (₹)</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unpaid.data.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.invoicenumber}</TableCell>
                      <TableCell>{inv.buyername || inv.accountid}</TableCell>
                      <TableCell>{ddmmyyyy(inv.createdat)}</TableCell>
                      <TableCell align="right">{inr(inv.totalamount)}</TableCell>
                      <TableCell align="right">{inr(inv.amountpaid)}</TableCell>
                      <TableCell align="right">
                        <strong>{inr(inv.amountdue)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined" onClick={() => startForInvoice(inv)}>
                          Record payment
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {mode === "advance" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Money received before an invoice exists is kept on the customer&apos;s account as an advance and
            settles their next invoice.
          </Typography>
          {accounts.isPending && <Typography>Loading customers…</Typography>}
          {accounts.isError && <ErrorAlert error={accounts.error} what="the customers" />}
          {accounts.isSuccess && accounts.data.length === 0 && (
            <Alert severity="info">There are no billing accounts in {ns} yet.</Alert>
          )}
          {accounts.isSuccess && accounts.data.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
              <TextField
                select
                size="small"
                label="Customer"
                value={advanceAccount}
                onChange={(e) => setAdvanceAccount(e.target.value)}
                sx={{ minWidth: 280 }}
              >
                {accounts.data.map((a) => (
                  <MenuItem key={a.accountid} value={a.accountid}>
                    {a.name || a.accountid}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" disabled={!advanceAccount} onClick={startAdvance}>
                Record advance
              </Button>
            </Stack>
          )}
        </Paper>
      )}

      <Dialog
        open={draft !== null}
        onClose={() => {
          if (!mutation.isPending) setDraft(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Record payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {label}
            {due !== null ? ` · due ₹${inr(due)}` : ""}
          </Typography>
          {draft && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  select
                  label="Method"
                  fullWidth
                  size="small"
                  value={draft.method}
                  onChange={(e) => set("method", e.target.value as PaymentMethod)}
                >
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="online" disabled>Pay online</MenuItem>
                </TextField>
                <PayOnlineNotice options={payOptions.data} readFailed={payOptions.isError} why="Pay online on this screen is coming next." />
              </Grid>
              {draft.method === "upi" && (
                <Grid size={{ xs: 12 }}>
                  <UpiPayQR options={payOptions.data} amount={Number(draft.amount) > 0 ? Number(draft.amount) : null} note="Payment" />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  label={draft.method === "upi" ? "UPI transaction id (UTR)" : "Voucher number (optional)"}
                  fullWidth
                  size="small"
                  value={draft.reference}
                  onChange={(e) => set("reference", e.target.value)}
                  slotProps={{ htmlInput: { "data-testid": "payment-reference" } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Amount (₹)"
                  fullWidth
                  size="small"
                  value={draft.amount}
                  onChange={(e) => set("amount", e.target.value)}
                  slotProps={{ htmlInput: { inputMode: "decimal", "data-testid": "payment-amount" } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Date received"
                  type="date"
                  fullWidth
                  size="small"
                  value={draft.receivedDate}
                  onChange={(e) => set("receivedDate", e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Time (IST)"
                  type="time"
                  fullWidth
                  size="small"
                  value={draft.receivedTime}
                  onChange={(e) => set("receivedTime", e.target.value)}
                  helperText="As the bank app shows it; blank if unknown"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          )}
          {attempted && problems.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </Box>
            </Alert>
          )}
          {mutation.isError && (
            <Box sx={{ mt: 2 }}>
              <ErrorAlert error={mutation.error} what="the payment" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={mutation.isPending} data-testid="record-payment">
            {mutation.isPending ? "Recording…" : "Record payment"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
